<template>
  <div>
    <template v-if="clicked">
      <v-text-field
        v-model.lazy="valor"
        v-money="vMoney"
        autofocus
        outlined
        class="fields pb-0"
        height="20px"
        dense
        hide-details
        :disabled="disabledFields || disabled"
        :counter="counter"
        :placeholder="text ? '' : '0'"
        :prefix="money ? 'R$' : ''"
        :suffix="percentage ? '%' : ''"
        :maxlength="!text ? (money ? 12 : 6) : counter"
        @keydown="handleKeydown"
        @keydown.tab.exact.prevent="toggleNext(0, 1)"
        @keydown.shift.tab.exact.prevent="toggleNext(0, -1)"
        @keydown.enter.exact.prevent="toggleNext(1, 0)"
        @keydown.shift.enter.exact.prevent="toggleNext(-1, 0)"
        @blur="toggleEdit"
      />
    </template>
    <mf-tooltip v-else top :disabled="disabledTooltip" max-width="300">
      <template v-slot:activator="{ on, attrs }">
        <span v-bind="attrs" class="field-padding" :class="{ 'field-error': error, 'field-disabled': disabled }" v-on="on" @click="toggleEdit">
          <div v-if="money">
            <span v-if="valor && valor !== '0,00'">{{ valor | localeString(2) | currency }}</span>
            <span v-else>{{ emptyMoneyLabel }}</span>
          </div>
          <span v-else-if="percentage">
            {{ valor && valor !== '0' ? (valor | localeString(0)) + '%' : '-' }}
          </span>
          <span v-else>
            {{ valor || '-' }}
          </span>
        </span>
      </template>
      <span>{{ tooltipText }}</span>
    </mf-tooltip>
  </div>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import { VMoney } from 'v-money'

export default {
  name: 'FieldToggle',
  directives: { money: VMoney },
  props: {
    value: { default: '' },
    money: { type: Boolean, default: false },
    percentage: { type: Boolean, default: false },
    defaultNumber: { type: Boolean, default: false },
    text: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    disabledFields: { type: Boolean, default: false },
    counter: { type: Number, default: 1000 },
    row: { type: Number, default: 0 },
    column: { type: Number, default: 0 },
    tooltip: { type: Boolean, default: false },
    tooltipText: { type: String, default: '' },
    limitValue: { type: Number, default: 0 },
    emptyMoneyLabel: { type: String, default: '-' },
    isIndustryCampaign: { type: Boolean, default: false },
    numbersOnly: { type: Boolean, default: false },
  },
  data: () => ({
    clicked: false,
    localValue: '',
    locale: localeString,
  }),
  computed: {
    vMoney() {
      if (this.money) return { decimal: ',', thousands: '.', precision: 2, masked: true }
      if (this.percentage && this.limitValue) return { decimal: ',', thousands: '.', precision: 0, masked: true }
      if (this.defaultNumber) return { decimal: ',', thousands: '.', precision: 0, masked: true }
      return false
    },
    valor: {
      get() {
        return this.localValue
      },
      set(value) {
        this.localValue = value

        if (this.money || this.percentage || this.defaultNumber) {
          const stringValue = String(value).replace(/[^\d,.-]/g, '')

          if (stringValue.match(/,\d{3,}/)) {
            return
          }

          const cleanString = stringValue.replace(/\./g, '').replace(',', '.')
          const numericValue = parseFloat(cleanString)
          this.$emit('input', isNaN(numericValue) ? 0 : numericValue)
        } else if (this.text) {
          this.$emit('input', value)
        } else {
          let MinAndMaxValue = parseFloat(
            value
              .toString()
              .replace(/[^0-9-.,]/g, '')
              .replace(',', '.')
          )
          if (isNaN(MinAndMaxValue)) MinAndMaxValue = 0
          this.$emit('input', MinAndMaxValue)
        }
      },
    },
    disabledTooltip() {
      return this.isIndustryCampaign ? !this.disabled : !this.tooltip || !this.tooltipText
    },
  },
  watch: {
    value(value) {
      this.localValue = this.money ? this.locale(value, 2) : value
    },
  },
  mounted() {
    this.localValue = this.money ? this.locale(this.value, 2) : this.value
    this.$bus.$on(`toggle-${this.row}-${this.column}`, this.toggleEdit)
  },
  beforeUnmount() {
    this.$bus.$off(`toggle-${this.row}-${this.column}`)
  },
  methods: {
    toggleEdit() {
      if (this.disabled) return
      this.clicked = !this.clicked
      if (this.limitValue !== 0) {
        if (this.valor.toString().length > 3) {
          this.valor = this.limitValue
        } else {
          this.valor = this.valor > this.limitValue ? this.limitValue : this.valor
        }
      }

      if ((this.money || this.percentage || this.defaultNumber) && this.valor < 0) {
        this.valor = 0
      }
    },
    toggleNext(deltaX, deltaY) {
      this.$bus.$emit(`toggle-${this.row + deltaX}-${this.column + deltaY}`, {})
    },
    handleKeydown(event) {
      if (this.numbersOnly) {
        const key = event.key
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End']

        const isDigit = /^[0-9]$/.test(key)
        const isAllowedKey = allowedKeys.includes(key)
        const isCtrlC = event.ctrlKey && key === 'c'
        const isCtrlV = event.ctrlKey && key === 'v'
        const isCtrlA = event.ctrlKey && key === 'a'

        if (!isDigit && !isAllowedKey && !isCtrlC && !isCtrlV && !isCtrlA) {
          event.preventDefault()
        }
      } else {
        if (event.key === '-') {
          event.preventDefault()
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.field-padding {
  span {
    padding: 5px;
    display: flex;
    width: 100%;
    height: 40px !important;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    border: 1px solid rgba(3, 3, 3, 0);
    white-space: normal;
    font-size: 14px;

    &:hover {
      border: 1px solid rgb(217, 217, 217, 1);
    }
  }
}

.field-error {
  span {
    border-color: $vermelho-0;
    color: $vermelho-0;
    &:hover {
      border-color: $vermelho-0;
      color: $vermelho-0;
    }
  }
}
.field-disabled {
  span {
    &:hover {
      border-color: transparent !important;
      cursor: text !important;
    }
  }
}
.v-input {
  font-size: 14px;
}
.v-input__slot {
  min-height: 22px !important;
}
</style>
